<template>
    <div
        :style="componentStyles"
        class="o-path-steps"
    >
        <div class="path-steps-info">
            <div class="path-steps-info-wrapper">
                <span :class="['path-steps-info-icon', iconCssClass]" />
                <span class="path-steps-info-mode" v-html="infoTitle" />
            </div>
            <button
                type="button"
                class="path-steps-overview-button"
                @click="buildPath"
            />
        </div>
        <div class="path-steps-wrapper">
            <ul v-if="activePath" class="path-steps-list">
                <li
                    v-for="(step, index) in activePath.steps"
                    :key="index"
                    :class="['path-steps-step', {'path-step-active': activeStepIndex === index}]"
                    @click="handleStepClick(index)"
                >
                    <div class="path-steps-index">
                        {{ index + 1}}
                    </div>
                    <OStepIcon
                        :item="item"
                        :index="index"
                        :step="step"
                    />
                    <OStepText :step="step"/>
                </li>
            </ul>
            <ul v-else>
                <li class="path-steps-step path-steps-step--placeholder"><OGuideStepPlaceholder/></li>
                <li class="path-steps-step path-steps-step--placeholder"><OGuideStepPlaceholder/></li>
                <li class="path-steps-step path-steps-step--placeholder"><OGuideStepPlaceholder/></li>
                <li class="path-steps-step path-steps-step--placeholder"><OGuideStepPlaceholder/></li>
            </ul>
        </div>
    </div>
</template>

<script>
import {
    mapState,
    mapGetters
} from "vuex";

import OStepIcon from '../../organisms/o-step-icon';
import OStepText from '../../unique-organisms/o-step-text/o-step-text';

import {
    START_VIEW_API,
    UI_MAP_READY,
    UI_STEP_BY_STEP_DIRECTIONS
} from "@/shared/components/map-viewer/constants/map-events";
import {
    EASY,
    FAST,
    NOELEVATORS
} from "@/utils/constants/app-constants";
import { TERMINAL_ID_CHANGED } from "@/utils/constants/bus-events-constants";
import { MAP_SHOW_PATH } from "@/shared/components/map-viewer/constants/map-commands";
import OGuideStepPlaceholder from "@/components/organisms/o-guide-step-placeholder/o-guide-step-placeholder";

export default {
    name: "OPathSteps",
    components: {
        OGuideStepPlaceholder,
        OStepIcon,
        OStepText
    },
    data(){
        return {
            guideLoadingTimeout: null,
        }
    },
    props: {
        itemId: {
            type: Number,
            required: true
        },
        view: {
            type: String,
            default: ''
        }
    },
    computed: {
        ...mapState({
            activePath: state => state.activePath,
            activeStepIndex: state => state.activeStepIndex
        }),
        ...mapGetters([
            't',
            'isAppLoaded',
            'getTerminalId',
            'getMapLoading',
            'getMapConfigProp',
            'getTerminalConfig',
            'getDenormalisedObjectById'
        ]),
        componentParams() {
            return this.getMapConfigProp(`views.${this.view}.mainFrame.OGuideSidebar.params.OPathSteps.params`) || null;
        },
        componentStyles() {
            return this.getMapConfigProp(`views.${this.view}.mainFrame.OGuideSidebar.params.OPathSteps.styles`) || null;
        },
        item() {
            return this.getDenormalisedObjectById(this.itemId);
        },
        mode() {
            const currentMode = this.$route.params.mode;

            // feature to avoid elevators due to Covid regulations
            if(currentMode === FAST){
                const isMapAvoidElevatorsMode = this.getMapConfigProp('guide.avoid_elevators', {});
                // eslint-disable-next-line camelcase
                const isObjectAvoidElevatorsMode = this.item?.meta?.avoid_elevators;

                if(isMapAvoidElevatorsMode || isObjectAvoidElevatorsMode)
                    return NOELEVATORS
            }

            return currentMode
        },
        infoTitle() {
            return this.$route.params.mode === EASY
                ? this.$t('Wheelchair Access <br> displayed on the map')
                : this.$t('Fastest route <br> displayed on the map');
        },
        iconCssClass() {
            return this.$route.params.mode === EASY
                ? 'path-steps-info-icon-easy'
                : 'path-steps-info-icon-fast';
        }
    },
    mounted() {
        this.initEventListeners();
        this.buildPathAfterLoading();
    },
    beforeDestroy() {
        this.deactivateEventListeners();
        clearTimeout(this.guideLoadingTimeout);
    },
    watch: {
        item() {
            this.buildPath();
        }
    },
    methods: {
        initEventListeners() {
            this.$bus.$on(UI_MAP_READY, this.checkMapReady);
            this.$bus.$on(UI_STEP_BY_STEP_DIRECTIONS, this.setActivePath);
            this.$bus.$on(TERMINAL_ID_CHANGED, this.buildPath);
        },
        deactivateEventListeners() {
            this.$bus.$off(UI_MAP_READY, this.checkMapReady);
            this.$bus.$off(UI_STEP_BY_STEP_DIRECTIONS, this.setActivePath);
            this.$bus.$off(TERMINAL_ID_CHANGED, this.buildPath);
        },
        checkMapReady() {
            if (this.isAppLoaded) this.buildPath();
        },
        buildPathAfterLoading() {
            if (this.isAppLoaded && !this.getMapLoading) {
                const delay = this.getMapConfigProp('guide.delay_guide', 0);
                this.guideLoadingTimeout = setTimeout(() => this.buildPath(), delay);
            }
        },
        setActivePath(data) {
            this.$store.commit('setActivePathToState', data);
            //this.setActiveStep(data.steps[0]);
            //this.setActiveStepIndex(0);
        },
        setActiveStep(step) {
            this.$store.commit('setActiveStep', step);
        },
        setActiveStepIndex(index) {
            this.$store.commit('setActiveStepIndex', index);
        },
        buildPath() {
            if(!this.item) return;

            this.$bus.$emit(START_VIEW_API);

            this.setActiveStepIndex(null);

            const params = {
                from:{
                    id: this.getTerminalId
                },
                to: {
                    id: this.itemId
                },
                mode: this.mode,
            };
            if( _.has(this.getTerminalConfig, 'cameras.kiosk_orientation'))
                params.initial_orientation = this.getTerminalConfig.cameras.kiosk_orientation;

            // eslint-disable-next-line camelcase
            if(this.item?.meta?.prioritise_outdoor)
                params.use_outside = 'prefer'

            params.portal_rules = this.getMapConfigProp('guide.portal_rules', {});

            this.$bus.$emit(MAP_SHOW_PATH, params);
        },
        handleStepClick(index) {
            this.setActiveStepIndex(index);
            this.$bus.$emit("map:showPathSteps", index);
        }
    }
}
</script>

<style lang="scss" src="./theme.scss" />
